/**
 * Последствия правки — до сохранения.
 *
 * Один доменный путь на все входы: экран настройки, советник и будущий
 * текстовый ввод должны показывать одно и то же, иначе у продукта появится
 * три разные правды об одном изменении.
 *
 * Порядок здесь принципиальный и обратный привычному «сохранил — увидел»:
 *
 * 1. человек правит факт;
 * 2. приложение считает последствия;
 * 3. человек подтверждает;
 * 4. факт сохраняется;
 * 5. появляется предложение новой раскладки.
 *
 * Если он сохранил факт, но новую раскладку не принял, факт всё равно
 * остаётся: это его данные, а не черновик приложения. Раскладка при этом
 * становится устаревшей, и об этом говорит карточка на главной.
 */
import {
  ZERO, add, allocationView, activeAllocation, clampPos, formatMoney,
  proposeAllocation, sub,
  type Goal, type Money, type Obligation,
} from '@skoka/core';
import { allocationViewOf, planInput, type AppState } from './store.js';

export interface ChangeEffect {
  /** Строки «было → станет». Показываются как есть. */
  rows: { label: string; before: Money; after: Money }[];
  /** Пояснения, которых числами не сказать. */
  notes: string[];
}

/** Состояние с применённой правкой — только для расчёта, ничего не пишет. */
const withPatch = (state: AppState, patch: Partial<AppState>): AppState => ({ ...state, ...patch });

/** Что человек может потратить сейчас: корзины, а при их отсутствии — план. */
function spendableOf(state: AppState): Money {
  const allocation = activeAllocation(state.allocations);
  if (!allocation) {
    const p = proposeAllocation(planInput(state));
    return add(p.lines.need, p.lines.want);
  }
  const view = allocationViewOf(state, allocation);
  return view.spendable;
}

/** Общие строки: что станет с деньгами при любой правке. */
function moneyRows(before: AppState, after: AppState): ChangeEffect['rows'] {
  const p0 = proposeAllocation(planInput(before));
  const p1 = proposeAllocation(planInput(after));
  return [
    { label: 'Доступно', before: spendableOf(before), after: spendableOf(after) },
    { label: 'Пока не распределено', before: p0.lines.unallocated, after: p1.lines.unallocated },
    { label: 'Не хватает', before: p0.shortfall, after: p1.shortfall },
  ];
}

/** Последствия правки обязательного платежа. */
export function obligationChange(
  state: AppState, id: string, patch: Partial<Obligation>,
): ChangeEffect {
  const before = state.obligations.find((o) => o.id === id);
  if (!before) return { rows: [], notes: [] };
  const after = withPatch(state, {
    obligations: state.obligations.map((o) => (o.id === id ? { ...o, ...patch } : o)),
  });

  const rows: ChangeEffect['rows'] = [
    { label: before.name, before: before.amount, after: patch.amount ?? before.amount },
    ...moneyRows(state, after),
  ];
  const notes: string[] = [];
  if (patch.isActive === false) notes.push('Платёж перестанет вычитаться из денег до прихода');
  if ((patch.amount ?? before.amount) > before.amount) {
    notes.push('Деньги повседневные нужды уменьшатся на разницу: платёж защищается раньше');
  }
  return { rows, notes };
}

/** Последствия правки цели. */
export function goalChange(state: AppState, id: string, patch: Partial<Goal>): ChangeEffect {
  const before = state.goals.find((g) => g.id === id);
  if (!before) return { rows: [], notes: [] };
  const next: Goal = { ...before, ...patch };
  const after = withPatch(state, {
    goals: state.goals.map((g) => (g.id === id ? next : g)),
  });

  const rows: ChangeEffect['rows'] = [
    { label: `Взнос в «${before.name}»`, before: before.monthlyContribution, after: next.monthlyContribution },
    ...moneyRows(state, after),
  ];

  // Срок цели здесь не называется: его называет прогноз в «Что изменится»
  // по месячному плану. Второй срок «по взносу» давал на одном экране
  // 5 января 2027 и 5 июня 2038 — два ответа на один вопрос.
  const notes: string[] = [];
  // Сколько уже внесено в этом месяце и сколько осталось — иначе человек не
  // понимает, требует ли приложение взнос заново.
  const month = state.today.slice(0, 7);
  const funded = add(...state.goalEvents
    .filter((e) => e.goalId === id && e.kind === 'contribution' && e.date.startsWith(month))
    .map((e) => e.amount));
  notes.push(
    `В этом месяце внесено ${formatMoney(funded)}, останется внести `
    + formatMoney(clampPos(sub(next.monthlyContribution, funded))),
  );
  const others = state.goals.filter((g) => g.id !== id && g.status === 'active');
  if (others.length > 0 && next.monthlyContribution > before.monthlyContribution) {
    notes.push(`Другим целям (${others.map((g) => g.name).join(', ')}) достанется меньше`);
  }
  return { rows, notes };
}

/** Показ эффекта одним блоком: одинаково на всех экранах. */
export function EffectRows({ effect }: { effect: ChangeEffect }) {
  // Только то, что меняется: «X → X» при нетронутой форме ничего не сообщает.
  const rows = effect.rows.filter((r) => r.before !== r.after);
  if (rows.length === 0) return null;
  return (
    <>
      <div className="pick"><div className="k">Было → станет</div></div>
      {rows.map((r) => (
        <div className="row" key={r.label}>
          <div className="rl">{r.label}</div>
          <div className="rv num">
            {formatMoney(r.before)} → {formatMoney(r.after)}
          </div>
        </div>
      ))}
      {effect.notes.map((n) => (
        <div className="note" key={n}>{n}</div>
      ))}
      {effect.rows.some((r) => r.label === 'Не хватает' && r.after > ZERO && r.after > r.before) && (
        <div className="note warn">
          После правки денег не хватит на защищённое. Раскладку придётся
          пересчитать и выбрать, что обеспечить.
        </div>
      )}
    </>
  );
}
